import template from './add_peer_members_to_objective_plan.html';
import * as Utils from '../../lib/savanna_utils.js';
import * as Permissions from '../../lib/permission_utils';

class AddPeerMembersObjPlanViewModel
{
    constructor(dialog)
    {
        this.dialog = dialog;
        this.date = ko.observable(moment().format('YYYY-MM-DD hh:mm'));
        this.title = ko.observable('Add peer members');
        this.from_date = ko.observable();
        this.to_date = ko.observable();
        this.company_id = ko.observable();
        this.editable = ko.observable(false);
        this.peer_group_id = ko.observable();
        this.peer_group = ko.observableArray();
        this.selected_group = ko.observable();
        this.add_peer_group = ko.observable(true); // Set to true by default
        
        // Add these missing observables that are needed for saving
        this.objective_plan_id = ko.observable();
        this.employment_info = ko.observable();
        
        // Add peer group employee management observables
        this.available_employees = ko.observableArray([]);
        this.peer_group_employees = ko.observableArray([]);
        this.selected_available_employees = ko.observableArray([]);
        this.selected_group_employees = ko.observableArray([]);

        // Add feature access observable
        this.has_admin_access = ko.observable(false);

        // Computed observables for display names
        this.available_employee_names = ko.computed(() => {
            return this.available_employees().map(emp => `${emp.firstname} ${emp.surname}`);
        });

        this.current_group_employees = ko.computed(() => {
            return this.peer_group_employees().map(emp => `${emp.firstname} ${emp.surname}`);
        });

        // Computed observable for the available employees header text
        this.available_employees_header = ko.computed(() => {
            return this.has_admin_access() ? 
                'Available employees' : 
                'Available employees (same department)';
        });

        this.view_date = ko.computed(() => {
            return 'View/Print Date:  ' + this.date();
        });

        // Subscribe to selected peer group changes
        this.selected_group.subscribe((val) => {
            if (this.selected_group()) {
                this.load_peer_group_employees();
            }
        });

        this.add_peer_group.subscribe((val) => {
            if (val) {
                // If no peer group is selected, load employees based on admin access
                if (!this.selected_group()) {
                    this.load_department_employees();
                }
            } else {
                // Clear employee lists when peer group is disabled
                this.available_employees([]);
                this.peer_group_employees([]);
                this.selected_available_employees([]);
                this.selected_group_employees([]);
            }
        });
    };

    async init_feature_access() {
        try {
            this.has_admin_access(await Permissions.checkAccessRights('PerformanceManagementAdmin'));
        } catch (error) {
            console.error('Error checking feature access:', error);
            this.has_admin_access(false);
        }
    }

    // Helper method to get employee object from display name
    get_employee_display_name(displayName) {
        return this.available_employees().find(emp => 
            `${emp.firstname} ${emp.surname}` === displayName);
    }

    // Add peers to the group
    btn_add_peers_click() {
        if (this.selected_available_employees().length === 0) return;
        
        let selected_employees = this.selected_available_employees().map(name => {
            return this.get_employee_display_name(name);
        }).filter(Boolean); // Filter out any undefined values
        
        // Add to peer group
        this.peer_group_employees.push(...selected_employees);
        
        // Remove from available employees
        this.available_employees(this.available_employees().filter(emp => 
            !selected_employees.some(selected => selected.employee_id === emp.employee_id)
        ));
        
        // Clear selection
        this.selected_available_employees([]);
    }

    // Remove peers from the group
    btn_remove_peers_click() {
        if (this.selected_group_employees().length === 0) return;
        
        let selected_employees = this.selected_group_employees().map(name => {
            // Remove trailing space from the name if any
            let fullName = name.trim();
            // Find the employee in peer_group_employees
            return this.peer_group_employees().find(emp => 
                `${emp.firstname} ${emp.surname}` === fullName
            );
        }).filter(Boolean); // Filter out any undefined values
        
        // Add back to available employees
        this.available_employees.push(...selected_employees);
        
        // Remove from peer group
        this.peer_group_employees(this.peer_group_employees().filter(emp => 
            !selected_employees.some(selected => selected.employee_id === emp.employee_id)
        ));
        
        // Clear selection
        this.selected_group_employees([]);
    }

    // Load employees based on admin access - all employees or department employees
    async load_department_employees() {
        try {
            let options = {
                table: 'v_active_employees_department',
                schema: 'savanna',
                limit: 1000000,
                fields: ['id_number', 'firstname', 'surname', 'employee_id', 'email', 'date_started', 'department_id']
            };

            // If user doesn't have admin access, filter by department
            if (!this.has_admin_access()) {
                let department_id;
                
                if (this.employment_info() && this.employment_info().employment_info && 
                    this.employment_info().employment_info.length > 0 && 
                    this.employment_info().employment_info[0].department_id) {
                    department_id = this.employment_info().employment_info[0].department_id;
                } else {
                    Grape.alerts.alert({ type: 'warning', message: 'Unable to determine department for loading employees.' });
                    return;
                }

                options.filter = [{
                    field: 'department_id',
                    operand: '=',
                    value: department_id
                }];
            } else {
                // For admin access, load all employees (no filter)
                options.filter = [{}];
            }
            
            let res = await Grape.fetches.getJSON('/api/record', options);
            if (res.status != 'ERROR' && res.records && Array.isArray(res.records)) {
                // Get current employee ID to exclude from available employees
                const currentEmployeeId = this.employment_info() ? this.employment_info().employee_id : null;
                
                // Filter out the current employee from available employees
                const availableEmployees = res.records.filter(emp => 
                    !(currentEmployeeId && emp.employee_id === currentEmployeeId)
                );
                
                // Set available employees
                this.available_employees(availableEmployees || []);
                
                // Initialize peer group employees as empty (no existing peer group)
                this.peer_group_employees([]);
                
                // Clear selections
                this.selected_available_employees([]);
                this.selected_group_employees([]);
            } else {
                // Handle case where no records are returned
                this.available_employees([]);
                this.peer_group_employees([]);
                this.selected_available_employees([]);
                this.selected_group_employees([]);
                
                if (res.status === 'ERROR') {
                    Grape.alerts.alert({ type: 'error', message: 'An error occurred while loading employees.' });
                }
            }
        } catch (error) {
            console.error(error);
            // Reset arrays to prevent undefined errors
            this.available_employees([]);
            this.peer_group_employees([]);
            this.selected_available_employees([]);
            this.selected_group_employees([]);
            
            Grape.alerts.alert({ type: 'error', message: 'An error occurred while loading employees: ' + error.message });
        }
    }
    
    // Updated load_peer_group_employees method with admin access consideration
   async load_peer_group_employees() {
    if (!this.selected_group()) return;

    try {
        let options = {
            table: 'v_active_employees_department',
            schema: 'savanna',
            limit: 1000000,
            fields: ['id_number', 'firstname', 'surname', 'employee_id', 'email', 'date_started', 'department_id']
        };

        // If user doesn't have admin access, filter by department
        if (!this.has_admin_access()) {
            let department_id;
            
            if (this.employment_info() && this.employment_info().employment_info && 
                this.employment_info().employment_info.length > 0 && 
                this.employment_info().employment_info[0].department_id) {
                // Use department_id from bindings employee_info (nested in employment_info array)
                department_id = this.employment_info().employment_info[0].department_id;
            } else {
                // Fall back to peer group's department_id
                let selected_peer_group = this.peer_group().find(group => group.peer_group_id === this.selected_group());
                if (!selected_peer_group) return;
                department_id = selected_peer_group.department_id;
            }

            options.filter = [{
                field: 'department_id',
                operand: '=',
                value: department_id
            }];
        } else {
            // For admin access, load all employees (no filter)
            options.filter = [{}];
        }
        
        let res = await Grape.fetches.getJSON('/api/record', options);
        if (res.status != 'ERROR' && res.records && Array.isArray(res.records)) {
            // Load existing peer group members from the v_peer_group view
            let existing_members_res = await Grape.fetches.getJSON('/api/record', {
                table: 'v_peer_group',
                schema: 'perfm_manage',
                fields: ['employee_id', 'full_name', 'peer_member_id'],
                filter: [{
                    field: 'peer_group_id',
                    operand: '=',
                    value: this.selected_group()
                }]
            });

            let existing_members = [];
            if (existing_members_res.status != 'ERROR' && existing_members_res.records && Array.isArray(existing_members_res.records)) {
                // Remove duplicates from existing members based on employee_id
                const uniqueMembers = new Map();
                existing_members_res.records.forEach(member => {
                    if (!uniqueMembers.has(member.employee_id)) {
                        // Find the full employee details from the main employee list
                        const employeeDetails = res.records.find(emp => emp.employee_id === member.employee_id);
                        
                        if (employeeDetails) {
                            // Use the full employee details instead of parsing the name
                            uniqueMembers.set(member.employee_id, {
                                ...employeeDetails,
                                peer_member_id: member.peer_member_id
                            });
                        } else {
                            // Fallback: parse the full_name if employee details not found
                            const nameParts = member.full_name ? member.full_name.split(' ') : ['', ''];
                            const firstname = nameParts[0] || '';
                            const surname = nameParts.slice(1).join(' ') || '';
                            
                            uniqueMembers.set(member.employee_id, {
                                employee_id: member.employee_id,
                                firstname: firstname,
                                surname: surname,
                                peer_member_id: member.peer_member_id
                            });
                        }
                    }
                });
                existing_members = Array.from(uniqueMembers.values());
            }

            // Filter out employees already in the peer group
            // Also exclude the current employee from available employees
            const currentEmployeeId = this.employment_info() ? this.employment_info().employee_id : null;
            const availableEmployees = res.records.filter(emp => 
                !existing_members.some(member => member.employee_id === emp.employee_id) &&
                !(currentEmployeeId && emp.employee_id === currentEmployeeId)
            );
            
            // Set available employees
            this.available_employees(availableEmployees || []);
            
            // Set current peer group members - this is the key fix
            this.peer_group_employees(existing_members || []);
            
            // Clear selections
            this.selected_available_employees([]);
            this.selected_group_employees([]);
            
            // Debug logging
            console.log('Existing members loaded:', existing_members);
            console.log('Peer group employees set:', this.peer_group_employees());
        } else {
            // Handle case where no records are returned
            this.available_employees([]);
            this.peer_group_employees([]);
            this.selected_available_employees([]);
            this.selected_group_employees([]);
            
            if (res.status === 'ERROR') {
                Grape.alerts.alert({ type: 'error', message: 'An error occurred while loading employees.' });
            }
        }
    } catch (error) {
        console.error(error);
        // Reset arrays to prevent undefined errors
        this.available_employees([]);
        this.peer_group_employees([]);
        this.selected_available_employees([]);
        this.selected_group_employees([]);
        
        Grape.alerts.alert({ type: 'error', message: 'An error occurred while loading employees: ' + error.message });
    }
}

    async load_existing_peer_members() {
        if (!this.objective_plan_id()) return;

        try {
            // First, check if there are any peer members for this objective plan
            let peer_member_res = await Grape.fetches.getJSON('/api/record', {
                table: 'peer_member',
                schema: 'perfm_manage',
                fields: ['employee_id', 'peer_member_id', 'objective_plan_id'],
                filter: [{
                    field: 'objective_plan_id',
                    operand: '=',
                    value: this.objective_plan_id()
                }]
            });

            console.log('existing peers for objective plan:', peer_member_res);

            if (peer_member_res.status !== 'ERROR' && peer_member_res.records && peer_member_res.records.length > 0) {
                // Enable peer group functionality
                this.add_peer_group(true);

                // Get employee details for the peer members
                const employee_ids = peer_member_res.records.map(member => member.employee_id);
                
                if (employee_ids.length > 0) {
                    let employee_details_res = await Grape.fetches.getJSON('/api/record', {
                        table: 'v_active_employees_department',
                        schema: 'savanna',
                        fields: ['id_number', 'firstname', 'surname', 'employee_id', 'email', 'date_started', 'department_id'],
                        filter: [{
                            field: 'employee_id',
                            operand: 'IN',
                            value: employee_ids
                        }]
                    });

                    if (employee_details_res.status !== 'ERROR' && employee_details_res.records) {
                        // Map peer member IDs to employee details
                        const peer_employees = employee_details_res.records.map(emp => {
                            const peer_member = peer_member_res.records.find(pm => pm.employee_id === emp.employee_id);
                            return {
                                ...emp,
                                peer_member_id: peer_member ? peer_member.peer_member_id : null
                            };
                        });

                        // Set the peer group employees
                        this.peer_group_employees(peer_employees);
                        
                        console.log('loaded existing peer members:', peer_employees);
                        
                        // Load employees for the available list (excluding current peer members and current employee)
                        await this.load_department_employees_for_edit();
                        
                        return true; // Indicate that we found existing peers
                    }
                }
            } else {
                // No existing peer members found
                console.log('No existing peer members found for objective plan:', this.objective_plan_id());
                this.peer_group_employees([]);
                return false; // Indicate no existing peers
            }
        } catch (error) {
            console.error('Error loading existing peer members:', error);
            Grape.alerts.alert({ 
                type: 'error', 
                message: 'An error occurred while loading existing peer members: ' + error.message 
            });
            return false;
        }
    }

    // Add this helper method to load employees for edit mode with admin access consideration
    async load_department_employees_for_edit() {
        try {
            let options = {
                table: 'v_active_employees_department',
                schema: 'savanna',
                limit: 1000000,
                fields: ['id_number', 'firstname', 'surname', 'employee_id', 'email', 'date_started', 'department_id']
            };

            // If user doesn't have admin access, filter by department
            if (!this.has_admin_access()) {
                let department_id;
                
                if (this.employment_info() && this.employment_info().employment_info && 
                    this.employment_info().employment_info.length > 0 && 
                    this.employment_info().employment_info[0].department_id) {
                    department_id = this.employment_info().employment_info[0].department_id;
                } else {
                    console.warn('Unable to determine department for loading employees.');
                    return;
                }

                options.filter = [{
                    field: 'department_id',
                    operand: '=',
                    value: department_id
                }];
            } else {
                // For admin access, load all employees (no filter)
                options.filter = [{}];
            }
            
            let res = await Grape.fetches.getJSON('/api/record', options);
            if (res.status !== 'ERROR' && res.records && Array.isArray(res.records)) {
                // Get current employee ID and existing peer member IDs to exclude
                const currentEmployeeId = this.employment_info() ? this.employment_info().employee_id : null;
                const existingPeerMemberIds = this.peer_group_employees().map(emp => emp.employee_id);
                
                // Filter out the current employee and existing peer members
                const availableEmployees = res.records.filter(emp => 
                    !(currentEmployeeId && emp.employee_id === currentEmployeeId) &&
                    !existingPeerMemberIds.includes(emp.employee_id)
                );
                
                // Set available employees
                this.available_employees(availableEmployees || []);
                
                // Clear selections
                this.selected_available_employees([]);
                this.selected_group_employees([]);
            }
        } catch (error) {
            console.error('Error loading employees for edit:', error);
            this.available_employees([]);
        }
    }

    // FIXED: This is the main save method that was broken
    async btn_save_peer_group() {
        // Validate that we have required data
        if (!this.objective_plan_id()) {
            Grape.alerts.alert({ type: 'warning', message: 'Objective Plan ID is required!' });
            return;
        }

        if (!this.employment_info() || !this.employment_info().employee_id) {
            Grape.alerts.alert({ type: 'warning', message: 'Employee information is required!' });
            return;
        }

        try {
            let requestData = {
                objective_plan_id: this.objective_plan_id(),
                employee_id: this.employment_info().employee_id,
            };

            // Add peer group data if enabled
            if (this.add_peer_group()) {
                // If a peer group is selected, include the peer_group_id
                if (this.selected_group()) {
                    requestData.peer_group_id = this.selected_group();
                }
            
                // Always include the peer members (whether from existing group or newly selected)
                requestData.peer_members = this.peer_group_employees().map(emp => ({
                    employee_id: emp.employee_id,
                    peer_member_id: emp.peer_member_id || null
                }));
            }

            console.log('request data', requestData);

            let res = await Grape.fetches.postJSON('/performance_management/objective_plan/edit', requestData);

            if (res.status != 'ERROR') {
                Grape.alerts.alert({ type: 'success', title: 'Success', message: 'Successfully saved peer members to Objective Plan' });
                this.dialog.close(true); // Pass true to indicate successful save
            }
            else {
                Grape.alerts.alert({ type: 'error', message: 'An error occurred while saving the peer members' });
            }
        } catch (error) {
            console.error('Error saving peer members:', error);
            Grape.alerts.alert({ type: 'error', message: 'An error occurred while saving: ' + error.message });
        }
    }

    close() {
        this.dialog.close();
    }
}

class AddPeerMembersObjePlanPage
{
    constructor(bindings)
    {
        this.viewModel = new AddPeerMembersObjPlanViewModel(this);
        this.bindings = bindings;
    };

    async init()
    {
        Utils.refresh_data_visible_roles();
        
        // Initialize feature access before other operations
        await this.viewModel.init_feature_access();
        
        // Set up bindings data
        if (this.bindings) {
            console.log('bindings', this.bindings);
            this.viewModel.employment_info(ko.mapping.toJS(this.bindings.employee_info));
            this.viewModel.objective_plan_id(this.bindings.objective_plan_id);
            
            // Always try to load existing peer members first
            const hasExistingPeers = await this.viewModel.load_existing_peer_members();
            
            // Set title based on whether we found existing peers or not
            if (hasExistingPeers) {
                this.viewModel.title('Edit Peer Members');
                this.viewModel.editable(true);
            } else {
                this.viewModel.title('Add Peer Members');
                this.viewModel.editable(false);
                // If no existing peers, load employees for adding new peer members
                await this.viewModel.load_department_employees();
            }
        }

        // Load peer groups
        try {
            let res = await Grape.fetches.getJSON('/api/record', {
                table: 'v_peer_group',
                schema: 'perfm_manage',
                filter: [{}]
            });
        
            if (res.status !== 'ERROR') {
                // Remove duplicate peer_group_name entries
                let unique_groups = [];
                let names_seen = new Set();
        
                for (const group of res.records) {
                    if (!names_seen.has(group.peer_group_name)) {
                        names_seen.add(group.peer_group_name);
                        unique_groups.push(group);
                    }
                }
        
                this.viewModel.peer_group(unique_groups);
            }
        } catch (error) {
            console.error(error);
            Grape.alerts.alert({
                type: 'error',
                title: 'Error',
                message: 'An error occurred when loading the Peer Groups'
            });
        }
    }

    async updateData() {
        // This method can be used for updating data if needed
    }
}

export default {
    name: 'AddPeerMembersObjePlan',
    dialog_class: AddPeerMembersObjePlanPage,
    template: template,
    template_filename: 'translation/dialogs/peer_members_to_objective_plan',
    provider: 'ps'
};